import React, { useRef } from 'react';
import { SegmentedToggleButton, SegmentedToggleStyled } from './styles';

export interface ISegmentedToggleOption {
  key: string,
  value: string,
}

interface IProps {
  name: string,
  value?: string,
  options: ISegmentedToggleOption[],
  handleChange: (name: string, value: string) => void,
  disabled?: boolean,
  className?: string,
  ariaLabel?: string,
}

const NEXT_KEYS = ['ArrowRight', 'ArrowDown'];
const PREV_KEYS = ['ArrowLeft', 'ArrowUp'];

export const SegmentedToggle = ({
  name,
  value,
  options,
  handleChange,
  disabled,
  className,
  ariaLabel,
}: IProps) => {
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const selectedIndex = options.findIndex(option => option.key === value);
  // Roving tab order: only the selected option (or the first one when nothing is selected) is reachable with Tab
  const tabStopIndex = selectedIndex >= 0 ? selectedIndex : 0;

  const selectOption = (index: number) => {
    const option = options[index];

    optionRefs.current[index]?.focus();

    if (option.key !== value) {
      handleChange(name, option.key);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    let nextIndex: number | null = null;

    if (NEXT_KEYS.includes(event.key)) {
      nextIndex = (index + 1) % options.length;
    } else if (PREV_KEYS.includes(event.key)) {
      nextIndex = (index - 1 + options.length) % options.length;
    } else if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = options.length - 1;
    }

    if (nextIndex !== null) {
      event.preventDefault();
      selectOption(nextIndex);
    }
  };

  return (
    <SegmentedToggleStyled className={className} role="radiogroup" aria-label={ariaLabel} aria-disabled={disabled}>
      {options.map((option, index) => (
        <SegmentedToggleButton
          key={option.key}
          ref={(element: HTMLButtonElement | null) => {
            optionRefs.current[index] = element;
          }}
          type="button"
          role="radio"
          aria-checked={option.key === value}
          tabIndex={index === tabStopIndex ? 0 : -1}
          active={option.key === value}
          disabled={disabled}
          className={`segmented-toggle__option segmented-toggle__option--${option.key}`}
          onClick={() => selectOption(index)}
          onKeyDown={(event: React.KeyboardEvent<HTMLButtonElement>) => handleKeyDown(event, index)}
        >
          {option.value}
        </SegmentedToggleButton>
      ))}
    </SegmentedToggleStyled>
  );
};

export default SegmentedToggle;
